/**
 * Centralized Timer Service for managing batched countdown timers
 * Implements the Singleton pattern to ensure only one instance exists
 */
class TimerService {
  constructor() {
    if (TimerService.instance) {
      return TimerService.instance;
    }

    TimerService.instance = this;
    this.initialize();
  }

  initialize() {
    // Store subscribers for each target time
    this.batches = new Map();
    
    // Store interval data for each target time
    this.intervals = new Map();
    
    // Update frequencies (in milliseconds)
    this.updateFrequencies = {
      day: 60000,     // Every minute for days
      hour: 60000,    // Every minute for hours
      minute: 1000,   // Every second for minutes
      second: 1000    // Every second for seconds
    };
  }

  /**
   * Calculate time remaining until target time
   */
  calculateTimeRemaining(targetTime) {
    if (!targetTime) return null;

    try {
      const target = new Date(targetTime).getTime();
      const now = new Date().getTime();
      const diffMs = target - now;
      const diffSeconds = Math.floor(diffMs / 1000);

      // Handle expired time
      if (diffSeconds <= 0) {
        return { value: 0, unit: 'second', isExpired: true };
      }

      // Calculate all units
      const days = Math.floor(diffSeconds / (24 * 60 * 60));
      const hours = Math.floor((diffSeconds % (24 * 60 * 60)) / (60 * 60));
      const minutes = Math.floor((diffSeconds % (60 * 60)) / 60);
      const seconds = diffSeconds % 60;

      // Return the most appropriate unit
      if (days > 0) {
        return { value: days, unit: 'day', isExpired: false };
      }
      if (hours > 0) {
        return { value: hours, unit: 'hour', isExpired: false };
      }
      if (minutes > 0) {
        return { value: minutes, unit: 'minute', isExpired: false };
      }
      return { value: seconds, unit: 'second', isExpired: false };
    } catch (error) {
      console.error('Error calculating time:', error);
      return null;
    }
  }

  /**
   * Subscribe to timer updates
   */
  subscribe(targetTime, callback) {
    if (!targetTime || typeof callback !== 'function') {
      return () => {};
    }

    try {
      // Initialize or get existing batch
      if (!this.batches.has(targetTime)) {
        this.batches.set(targetTime, new Set());
      }

      const subscribers = this.batches.get(targetTime);
      subscribers.add(callback);

      // Start timer if not already running
      if (!this.intervals.has(targetTime)) {
        this.startTimer(targetTime);
      }

      // Log active timers and their subscribers
      console.log('Active Timers:', {
        totalTimers: this.intervals.size,
        timers: Array.from(this.batches.entries()).map(([time, subs]) => ({
          time,
          subscribers: subs.size
        }))
      });

      // Return unsubscribe function
      return () => {
        try {
          const subscribers = this.batches.get(targetTime);
          if (subscribers) {
            subscribers.delete(callback);

            if (subscribers.size === 0) {
              this.cleanupTimer(targetTime);
              console.log('Timer cleaned up. Remaining timers:', this.intervals.size);
            }
          }
        } catch (error) {
          console.error('Error during unsubscribe:', error);
        }
      };
    } catch (error) {
      console.error('Error during subscribe:', error);
      return () => {};
    }
  }

  /**
   * Start a shared timer for a target time
   */
  startTimer(targetTime) {
    try {
      const updateBatch = () => {
        const timeLeft = this.calculateTimeRemaining(targetTime);
        if (!timeLeft) return;

        // Notify all subscribers
        const subscribers = this.batches.get(targetTime);
        if (subscribers) {
          subscribers.forEach(callback => {
            try {
              callback(timeLeft);
            } catch (error) {
              console.error('Error in subscriber callback:', error);
            }
          });
        }

        // Handle expired timer
        if (timeLeft.isExpired) {
          this.cleanupTimer(targetTime);
          return;
        }

        // Update interval frequency if needed
        const newFrequency = this.updateFrequencies[timeLeft.unit];
        const currentInterval = this.intervals.get(targetTime);

        if (currentInterval && currentInterval.frequency !== newFrequency) {
          clearInterval(currentInterval.id);
          const newIntervalId = setInterval(updateBatch, newFrequency);
          this.intervals.set(targetTime, { id: newIntervalId, frequency: newFrequency });
        }
      };

      // Calculate initial frequency
      const initialTime = this.calculateTimeRemaining(targetTime);
      if (!initialTime) return;

      const frequency = this.updateFrequencies[initialTime.unit];
      const intervalId = setInterval(updateBatch, frequency);
      this.intervals.set(targetTime, { id: intervalId, frequency });
      
      // Run first update immediately
      updateBatch();
    } catch (error) {
      console.error('Error starting timer:', error);
    }
  }

  /**
   * Clean up timer and its subscribers
   */
  cleanupTimer(targetTime) {
    try {
      const interval = this.intervals.get(targetTime);
      if (interval) {
        clearInterval(interval.id);
        this.intervals.delete(targetTime);
      }
      this.batches.delete(targetTime);
    } catch (error) {
      console.error('Error cleaning up timer:', error);
    }
  }

  /**
   * Reset service state
   */
  reset() {
    try {
      // Clear all intervals
      this.intervals.forEach(({ id }) => clearInterval(id));
      this.initialize();
    } catch (error) {
      console.error('Error resetting service:', error);
    }
  }
}

// Create and export singleton instance
const timerService = new TimerService();
Object.freeze(timerService);
export { timerService }; 